
import { useEffect, useRef } from 'react';
import useMemorySafe from '@/hooks/use-memory-safe';

/**
 * Performance optimizer component that applies runtime optimizations
 * This helps improve web vitals with zero overhead in production
 */
const PerformanceOptimizer = () => {
  // Ultra minimal memory tracking
  useMemorySafe('PerformanceOptimizer');
  
  // Track if we've already run optimizations
  const hasRunOptimizationsRef = useRef(false);
  
  // Run performance optimizations on mount
  useEffect(() => {
    // Skip duplicate runs
    if (hasRunOptimizationsRef.current) return;
    hasRunOptimizationsRef.current = true;
    
    // Skip detailed optimizations in production
    const isProduction = process.env.NODE_ENV === 'production';
    
    // Better browser support and performance with native attributes 
    const optimizeImages = () => {
      // Use native browser features for image optimization
      if ('loading' in HTMLImageElement.prototype) {
        document.querySelectorAll('img:not([loading])')
          .forEach(img => {
            img.setAttribute('loading', 'lazy');
            
            // Add decoding attribute if not set
            if (!img.hasAttribute('decoding')) {
              img.setAttribute('decoding', 'async');
            }
          });
      }
    };
    
    // Setup intersection observer for lazy-loaded components
    const setupIntersectionObserver = () => {
      if ('IntersectionObserver' in window) {
        const lazyLoadObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const lazyElement = entry.target;
              
              // If this is a lazy-load container, trigger its load
              if (lazyElement.classList.contains('lazy-load-container')) {
                lazyElement.classList.remove('lazy-load-container');
                lazyElement.classList.add('lazy-loaded');
                
                // Dispatch custom event that lazy load components can listen to
                const event = new CustomEvent('lazyload-trigger');
                lazyElement.dispatchEvent(event);
              }
              
              // Stop observing this element
              lazyLoadObserver.unobserve(lazyElement);
            }
          });
        }, {
          rootMargin: '300px', // Increased to load even earlier
          threshold: 0.01
        });
        
        // Observe all lazy-load containers
        document.querySelectorAll('.lazy-load-container').forEach(el => {
          lazyLoadObserver.observe(el);
        });
      }
    };
    
    // Preload critical resources
    const preloadCriticalResources = () => {
      const preloadList = [
        { href: '/lovable-uploads/5ff0c1a6-9b40-485d-bb61-6427fd6e304e.png', as: 'image' },
      ];
      
      preloadList.forEach(resource => {
        // Check if already preloaded
        if (!document.querySelector(`link[rel="preload"][href="${resource.href}"]`)) {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.href = resource.href;
          link.as = resource.as;
          document.head.appendChild(link);
        }
      });
    };
    
    // Run optimization when page is idle or with a delay
    const runOptimizations = () => {
      optimizeImages();
      setupIntersectionObserver();
      if (isProduction) {
        preloadCriticalResources();
      }
    };
    
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(runOptimizations, { timeout: 2000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(runOptimizations, isProduction ? 1000 : 500);
    }
    
    // No cleanup needed - these are one-time optimizations
  }, []);
  
  return null; // This is a non-visual component
};

export default PerformanceOptimizer;
